import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import Swal from 'sweetalert2';
// The dark badge logo — the plain one is white-on-transparent for the dark sidebar.
import logo from '../../assets/images/logo-dark.png';
import AuthService from '../../services/AuthService';
import ApiService from '../../services/ApiService';
import PushService from '../../services/PushService';
import CheckInOut from '../CheckInOut';
import { hasPermission, getDefaultAdminRoute } from '../../utils/permissions';
import { isNativePlatform } from '../../utils/platform';
import { useAdminCounts } from '../../hooks/useAdminCounts';
import { TAB_CANDIDATES, TAB_LIMIT, TAB_LIMIT_NO_FAB, FAB, MORE_GROUPS, findMenuItem } from './appMenu';
import './AppShell.css';

// Matches the web header's notification polling (Header.jsx).
const NOTIFICATION_POLL_MS = 30000;

// Class put on a page's own <h1> once the top bar has taken over its text.
const LIFTED_HEADING = 'appshell-lifted-heading';

const Badge = ({ count }) => {
    if (!count) return null;
    return <span className="appshell-badge">{count > 99 ? '99+' : count}</span>;
};

const avatarFallback = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'A')}&background=4F97D8&color=fff&size=96`;

/**
 * Admin portal chrome for the app build: a top bar, the page, and a bottom
 * tab bar with a raised "+" (new manual booking) and a More sheet for the rest
 * of the menu. Replaces Header + Sidebar when REACT_APP_BUILD_FOR=app (see
 * router/Layout).
 */
const AppShell = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const [moreOpen, setMoreOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pageTitle, setPageTitle] = useState('');
    const { subscriberCount, cancellationCount, bookingCount } = useAdminCounts();
    const counts = { bookings: bookingCount, subscribers: subscriberCount, cancellations: cancellationCount };
    const admin = AuthService.getCurrentUser();

    const { tabs, moreGroups, showFab } = useMemo(() => {
        const showFab = hasPermission(FAB.permission);
        const tabs = TAB_CANDIDATES
            .filter((item) => hasPermission(item.permission))
            .slice(0, showFab ? TAB_LIMIT : TAB_LIMIT_NO_FAB);
        const tabPaths = new Set(tabs.map((item) => item.path));
        const moreGroups = MORE_GROUPS
            .map((group) => ({
                ...group,
                items: group.items.filter((item) => hasPermission(item.permission) && !tabPaths.has(item.path)),
            }))
            .filter((group) => group.items.length > 0);
        return { tabs, moreGroups, showFab };
    }, []);

    const path = location.pathname.replace(/\/$/, '');
    const moreItems = moreGroups.flatMap((group) => group.items);
    const currentItem = findMenuItem(path, [...tabs, ...moreItems]);
    const isHome = path === '/admin/dashboard';
    // Anything below a menu entry (a detail or edit screen) gets a back arrow.
    const showBack = !currentItem || path !== currentItem.path;
    // "More" lights up for any screen reached from the sheet rather than a tab.
    const moreActive = !!currentItem && !tabs.includes(currentItem);
    const moreCount = moreItems.reduce((sum, item) => sum + (counts[item.countKey] || 0), 0);
    const title = pageTitle || currentItem?.title || 'Admin';

    // Scoped styles (hidden breadcrumbs, full-width tables, …) key off this.
    useEffect(() => {
        document.body.classList.add('app-build');
        return () => document.body.classList.remove('app-build');
    }, []);

    // Booking alerts as phone pushes: register this device once signed in.
    // Tapping a booking push opens Bookings; anything else opens the inbox.
    useEffect(() => {
        PushService.register((data) => {
            navigate(data.type === 'booking' ? '/admin/bookings' : '/admin/notifications');
        });
    }, [navigate]);

    // Close the sheet and start at the top whenever navigation happens.
    useEffect(() => {
        setMoreOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // Lift the page's own heading ("Edit Role", "Booking BK-…") into the top
    // bar, like a native navigation title, and hide it in the page. Pages
    // render after their data loads, so keep watching until one appears.
    useEffect(() => {
        setPageTitle('');
        const root = contentRef.current;
        if (!root) return undefined;
        const lift = () => {
            const heading = root.querySelector('h1.page-heading, h1.bookings-title');
            if (!heading) return;
            heading.classList.add(LIFTED_HEADING);
            setPageTitle(heading.textContent.trim());
        };
        lift();
        const observer = new MutationObserver(lift);
        observer.observe(root, { childList: true, subtree: true, characterData: true });
        return () => observer.disconnect();
    }, [location.pathname]);

    useEffect(() => {
        const getUnread = async () => {
            try {
                const response = await ApiService.request({
                    method: 'GET',
                    url: 'admin/getNotifications',
                    params: { page: 1, limit: 1 },
                });
                if (response.data.status) setUnreadCount(response.data.data?.unread_count || 0);
            } catch (error) {
                console.log('Error fetching notifications:', error);
            }
        };
        getUnread();
        const interval = setInterval(getUnread, NOTIFICATION_POLL_MS);
        return () => clearInterval(interval);
    }, []);

    // Android hardware back: close the sheet, else go back, else leave the app.
    useEffect(() => {
        if (!isNativePlatform()) return undefined;
        const listener = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            // An open slide-in panel closes first, via its own Back button.
            const panelBack = document.querySelector('.offcanvas.show [data-app-back]');
            if (panelBack) panelBack.click();
            else if (moreOpen) setMoreOpen(false);
            else if (canGoBack) navigate(-1);
            else CapacitorApp.exitApp();
        });
        return () => { listener.then((handle) => handle.remove()); };
    }, [moreOpen, navigate]);

    const handleLogout = async () => {
        const result = await Swal.fire({
            title: 'Confirm Logout',
            text: 'Are you sure you want to log out of your session?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4f97d8',
            confirmButtonText: 'Logout',
            cancelButtonColor: '#9E9B9B',
        });
        if (result.isConfirmed) AuthService.logout();
    };

    const renderTab = (tab) => (
        <NavLink key={tab.path} to={tab.path} className="appshell-tab">
            {({ isActive }) => (
                <>
                    <span className="appshell-tab-icon">
                        <i className={isActive ? tab.activeIcon : tab.icon}></i>
                        <Badge count={counts[tab.countKey]} />
                    </span>
                    <span className="appshell-tab-label">{tab.title}</span>
                </>
            )}
        </NavLink>
    );
    // The "+" sits in the middle of the bar: two tabs, "+", the rest, More.
    const fabIndex = Math.min(2, tabs.length);

    return (
        <div className="appshell">
            <header className={`appshell-topbar ${isHome ? 'is-home' : ''}`}>
                {isHome ? (
                    <Link to={getDefaultAdminRoute()} className="appshell-brand">
                        <img src={logo} alt="" />
                        <span>
                            <strong>Boost Area</strong>
                            <small>Business Portal</small>
                        </span>
                    </Link>
                ) : (
                    <>
                        {showBack && (
                            <button type="button" className="appshell-icon-btn appshell-back" onClick={() => navigate(-1)} aria-label="Back">
                                <i className="bi bi-arrow-left"></i>
                            </button>
                        )}
                        <h1 className="appshell-title">{title}</h1>
                    </>
                )}
                <div className="appshell-actions">
                    <CheckInOut />
                    {hasPermission('notifications.index') && (
                        <button
                            type="button"
                            className="appshell-icon-btn appshell-bell"
                            onClick={() => navigate('/admin/notifications')}
                            aria-label="Notifications"
                        >
                            <i className="bi bi-bell"></i>
                            <Badge count={unreadCount} />
                        </button>
                    )}
                    {isHome && hasPermission('account.view') && (
                        <Link to="/admin/account" className="appshell-avatar" aria-label="Account">
                            <img
                                src={admin?.picture || avatarFallback(admin?.name)}
                                alt=""
                                onError={(e) => { e.currentTarget.src = avatarFallback(admin?.name); }}
                            />
                        </Link>
                    )}
                </div>
            </header>

            <main className="appshell-content" ref={contentRef}>{children}</main>

            <nav className={`appshell-tabbar ${showFab ? 'has-fab' : ''}`}>
                {tabs.slice(0, fabIndex).map(renderTab)}
                {showFab && (
                    <div className="appshell-fab-slot">
                        <button
                            type="button"
                            className="appshell-fab"
                            onClick={() => navigate(FAB.path)}
                            aria-label="New booking"
                        >
                            <i className="bi bi-plus-lg"></i>
                        </button>
                    </div>
                )}
                {tabs.slice(fabIndex).map(renderTab)}
                <button
                    type="button"
                    className={`appshell-tab ${moreActive || moreOpen ? 'active' : ''}`}
                    onClick={() => setMoreOpen((open) => !open)}
                >
                    <span className="appshell-tab-icon">
                        <i className={moreActive || moreOpen ? 'bi bi-grid-fill' : 'bi bi-grid'}></i>
                        <Badge count={moreCount} />
                    </span>
                    <span className="appshell-tab-label">More</span>
                </button>
            </nav>

            {moreOpen && (
                <>
                    <div className="appshell-sheet-backdrop" onClick={() => setMoreOpen(false)} />
                    <div className="appshell-sheet" role="dialog" aria-label="More">
                        <div className="appshell-sheet-handle" />
                        <div className="appshell-sheet-profile">
                            <img
                                src={admin?.picture || avatarFallback(admin?.name)}
                                alt=""
                                onError={(e) => { e.currentTarget.src = avatarFallback(admin?.name); }}
                            />
                            <span>
                                <strong>{admin?.name || 'Admin'}</strong>
                                <small>{admin?.email || ''}</small>
                            </span>
                        </div>
                        {moreGroups.map((group) => (
                            <section key={group.title} className="appshell-sheet-group">
                                <h2 className="appshell-sheet-heading">{group.title}</h2>
                                <div className="appshell-sheet-grid">
                                    {group.items.map((item) => (
                                        <NavLink key={item.path} to={item.path} className="appshell-sheet-item">
                                            <span className="appshell-sheet-icon">
                                                <i className={item.icon}></i>
                                                <Badge count={counts[item.countKey]} />
                                            </span>
                                            <span className="appshell-sheet-label">{item.title}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            </section>
                        ))}
                        <button type="button" className="appshell-logout" onClick={handleLogout}>
                            <i className="bi bi-box-arrow-right"></i> Logout
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AppShell;
